'use client';
import React from 'react';
import CurveContainer from '@elements/curve-container';
import Div from '@pezeshk-book/ui-kit/div';
import Breadcrumbs from '@pezeshk-book/ui-kit/breadcrumbs';
import routes from '@routes';
import Image from '@elements/image';
import PostActions from '../post-actions';
import Link from 'next/link';
import getParseRoute from '@utils/helpers/parse-route';
import Text from '@pezeshk-book/ui-kit/text';
import ShareBox from '@modules/share-box';
import process from 'process';
import SynchronizeIcon from '@icons-components/synchronize-icon';
import convertDate from '@utils/time/convert-date';
import HtmlRenderer from '@modules/html-renderer';
import useSWR from 'swr';
import postItemApi from '@api/posts/post-item';
import PostItemSchema from '@schema/post/post-item';
import RelatedPosts from '@layouts/posts/related-posts';
import Faq from '@layouts/faq-services';
import { useParams } from 'next/navigation';
import getDictionary from '@dictionaries-client';
import { LocaleTypes } from '@dictionaries-types';

const PostDetails = ({ postItem, slug }: {postItem: any, slug: string}) => {
  const { lang } = useParams<{lang: LocaleTypes}>();
  const { posts, home } = getDictionary(lang);
  const response = async () => {
    return await postItemApi({ slug: decodeURI(slug) });
  };

  const { data, error } = useSWR(`api/client/v1/posts/show/${decodeURI(slug)}`, response, {
    fallback: {
      [`api/client/v1/posts/show/${decodeURI(slug)}`]: postItem,
    },
  });

  if (error) {
    return null;
  }

  return (
    <>
      <CurveContainer firstContainer={true}>
        <Div itemScope itemType={'https://schema.org/BlogPosting'} className={'flex-col w-full'}>
          <Breadcrumbs
            dir={lang === 'fa' ? 'rtl' : 'ltr'}
            color={'tertiary'}
            homeTitle={home.label}
            className={'mb-10'}
            breadcrumbsData={[
              {
                label: posts.label,
                path: getParseRoute({ pathname: routes['route.posts.index'], locale: lang }),
              },
              {
                label: data?.type?.title,
                path: getParseRoute({ pathname: `/posts/categories/${data?.type?.slug}`, locale: lang }),
              },
              {
                label: data?.title,
              },
            ]}
          />
          {data?.image?.key_0?.url ? (
            <Div className={'relative w-full h-36 sm:h-44 md:h-80'}>
              <Image fill={true} sizes={'50vw'} className={'object-cover rounded-tl-[20px] sm:rounded-tl-[58px] md:rounded-tl-[100px] rounded-sm sm:rounded md:rounded-tr-lg'} alt={data?.title} src={data?.image?.key_0?.url}/>
            </Div>
          ) : null}
          <PostActions
            hits={data?.hits}
            id={data?.id}
            like={data?.like}
            dislike={data?.dislike}
          />
          <Div className={'mt-7 mb-4 md:mb-7 flex items-center before:mr-2 before:w-2 before:h-full before:bg-tertiary-500'}>
            <Div className={'flex-col'}>
              <Link passHref={true} href={getParseRoute({ pathname: routes['route.posts.category'], query: { slug: data?.type?.slug }, locale: lang })}>
                <Text align={'start'} color={'tertiary'}>{data?.type?.title}</Text>
              </Link>
              <Text itemProp="name headline" align={'start'} type={'bold'} typography={'xl'}>{data?.title}</Text>
            </Div>
          </Div>
          <Div className={'w-full justify-start mt-4 gap-x-2 sm:gap-x-12'}>
            <ShareBox
              translate={posts.details.share_title}
              title={data?.title}
              link={`${process.env.HOST}/posts/${data?.slug}`}
            />
            <Div className={'gap-1 items-center'}>
              <Div className={'relative w-4 h-4 text-grey-500'}>
                <SynchronizeIcon/>
              </Div>
              <Text color={'grey.500'} typography={'sm'} className={'whitespace-nowrap'}>{posts.details.update}</Text>
              <Text color={'grey.500'} typography={'sm'} className={'whitespace-nowrap'}>{convertDate(data?.published_at, 'DD MMMM YYYY', lang)}</Text>
            </Div>
          </Div>
          <Div className={'w-full'} itemProp="articleBody">
            <Div className={'flex-col'}>
              <HtmlRenderer chaptersTitle={posts.details.chapters} html={data?.content}/>
            </Div>
            <PostItemSchema
              slug={data?.slug}
              publishedAt={data?.published_at}
              image={data?.image?.key_0?.url}
            />
          </Div>
          {/*<Div className={'relative w-full h-[260px] md:h-[337px] sm:h-[181px] mt-14 mb-11 sm:mb-20 md:mb-24'}>
            <Image alt={'image'} src={RelatedImage} className={'rounded-lg md:rounded-2xl'}/>
          </Div>*/}
        </Div>
      </CurveContainer>
      {/*<CurveContainer gray={true}>
        <Div className={'w-full flex-col'}>
          <Text type={'bold'} typography={'xl'} align={'start'} className={'mb-10 flex items-center before:ml-2 before:w-2 before:h-7 before:bg-tertiary-500'}>
            در این مقاله مطالعه می‌کنید:
          </Text>
          <Article articleData={articleData}/>
        </Div>
      </CurveContainer>*/}

      {data?.faqs && data?.faqs.length > 0 ? (
        <CurveContainer gray={true}>
          <Div itemScope itemType="https://schema.org/FAQPage" className={'flex-col w-full'}>
            <Text type={'bold'} typography={'xl'} align={'start'} className={'mb-10 flex items-center before:mr-2 before:w-2 before:h-7 before:bg-tertiary-500'}>
              {posts.details.faq_title}
            </Text>
            <Faq faqData={data?.faqs}/>
          </Div>
        </CurveContainer>
      ) : null}

      {data?.similar?.data && data?.similar?.data?.length > 0 ? (
        <CurveContainer gray={false}>
          <Div className={'w-full flex-col'}>
            <Text type={'bold'} typography={'xl'} align={'start'} className={'mb-10 flex items-center before:mr-2 before:w-2 before:h-7 before:bg-tertiary-500'}>
              {posts.details.similar_posts_title}
            </Text>
            <RelatedPosts item={data?.similar?.data}/>
          </Div>
        </CurveContainer>
      ) : null}
    </>
  );
};

export default PostDetails;
